import MaterialIcon from '@components/common/MaterialIcon';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import MultiSelectionDropdown from '@components/tailwind-components/MultiSelectionDropdownT';
import { useLLMBlockSelector } from '@hooks/flow/block/useLLMBlockSelector';
import type { DataSourceAutomaticallyIncludeInLLMProps } from '@interfaces/TDataSourceGeneration';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export const DataSourceAutomaticallyIncludeInLLMContent: React.FC<
  DataSourceAutomaticallyIncludeInLLMProps
> = ({
  automaticallyIncludeInLLM,
  setAutomaticallyIncludeInLLM,
  selectedLLMBlocks,
  setSelectedLLMBlocks,
  dataSourceSelection,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const sharedWithCompanyIds =
    dataSourceSelection.existingFile?.sharedWithCompanyIds;
  const existingFileKey = dataSourceSelection.existingFile?.key;

  const { allBlocks, blockTranslations } = useLLMBlockSelector({
    enabled: automaticallyIncludeInLLM,
    sharedWithCompanyIds,
  });

  // Pre-select blocks that already include this data source.
  const preSelectionApplied = useRef(false);
  useEffect(() => {
    if (preSelectionApplied.current || !allBlocks.length || !existingFileKey) {
      return;
    }
    preSelectionApplied.current = true;
    const preSelected = allBlocks
      .filter((block) => block.dataSources.includes(existingFileKey))
      .map((block) => ({ blockId: block.blockId, companyId: block.companyId }));
    setSelectedLLMBlocks(preSelected);
  }, [allBlocks, existingFileKey, setSelectedLLMBlocks]);

  const selectedBlockIds = selectedLLMBlocks.map(
    (selectedBlock) => selectedBlock.blockId,
  );

  const handleToggleBlock = (blockId: string) => {
    const block = allBlocks.find(
      (availableBlock) => availableBlock.blockId === blockId,
    );
    if (!block) {
      return;
    }
    setSelectedLLMBlocks((prev) =>
      prev.some((existingBlock) => existingBlock.blockId === blockId)
        ? prev.filter((existingBlock) => existingBlock.blockId !== blockId)
        : [...prev, { blockId, companyId: block.companyId }],
    );
  };

  const handleAutoIncludeChange = (checked: boolean) => {
    setAutomaticallyIncludeInLLM(checked);
  };

  return (
    <div className='h-full flex flex-col rounded-lg bg-gray-100 overflow-hidden border border-primary-dark'>
      <div className='flex bg-primary-dark text-white text-base font-normal py-1 px-3'>
        Step 3 - {t('data-sources.manual-training.subtitle')}
      </div>

      <div className='flex flex-col p-3 gap-3'>
        <div className='flex items-center gap-2 text-primary-base'>
          <MaterialIcon
            icon='info'
            classes={[isDisabled ? 'text-gray-300' : 'text-primary-base']}
          />
          <span className='text-tiny italic text-gray-400'>
            {t('data-sources.manual-training.info')}
          </span>
        </div>

        <div className='flex items-center gap-2 ml-3 text-primary-base'>
          <CheckboxTogglerT
            toggleSize='base'
            label={t('data-sources.manual-training.auto-include')}
            field='isEnabled'
            colorScheme='default'
            labelClassName='text-primary-base text-sm font-medium'
            id='include-in-llm'
            checked={automaticallyIncludeInLLM}
            onChange={(event) => {
              handleAutoIncludeChange(event.target.checked);
            }}
            options={{
              disabled: isDisabled,
            }}
          />
        </div>

        {isDisabled && (
          <div className='flex items-start gap-2 ml-3 p-2 bg-yellow-50 border border-yellow-200 rounded'>
            <MaterialIcon
              icon='lock'
              classes={['text-yellow-600', 'text-sm']}
            />
            <span className='text-tiny italic'>
              {t('data-sources.manual-training.disabled-for-labels')}
            </span>
          </div>
        )}

        {automaticallyIncludeInLLM && !isDisabled && (
          <div className='flex flex-col text-primary-base border-t border-gray-200 pt-2'>
            <div className='flex items-center gap-2'>
              <MaterialIcon icon='info' />
              <span className='text-tiny italic text-gray-400'>
                {t('data-sources.manual-training.auto-include-info')}
              </span>
            </div>
            <div className='flex flex-col gap-1 mt-3 ml-3'>
              <span className='text-xs font-medium text-primary-base'>
                {t('data-sources.manual-training.select-llm-blocks')}
              </span>
              <MultiSelectionDropdown
                selectedValues={selectedBlockIds}
                availableValues={allBlocks.map((block) => block.blockId)}
                valueTranslations={blockTranslations}
                placeholder={t(
                  'data-sources.manual-training.select-llm-blocks-placeholder',
                )}
                selectValue={handleToggleBlock}
                keepOpen={true}
                maxVisibleBadges={4}
                autoSizeBadges={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataSourceAutomaticallyIncludeInLLMContent;
